import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Lock, ChevronDown, ChevronUp, Calendar, Star, ArrowRight } from './CustomIcons';

const PHASES = [
  {
    id: '90d',
    label: '90 Days',
    sublabel: 'Foundation',
    color: '#F97316',
    tasks: [
      { id: 't1', title: 'Enroll in a vocational pathway', desc: 'Choose your primary career pathway in the Learn module.', priority: 'high' },
      { id: 't2', title: 'Complete intake assessment', desc: 'Work with your case manager to identify education and employment goals.', priority: 'high' },
      { id: 't3', title: 'Earn first 100 Credits', desc: 'Complete 3 lessons to build your foundation and reach 100 credits.', priority: 'medium' },
      { id: 't4', title: 'Contact family or support network', desc: 'Identify 1–2 people who will support your reentry.', priority: 'high' },
      { id: 't5', title: 'Set a savings goal', desc: 'Decide how many credits to convert to Reentry Savings each week.', priority: 'medium' },
    ]
  },
  {
    id: '60d',
    label: '60 Days',
    sublabel: 'Building',
    color: '#F97316',
    tasks: [
      { id: 't6', title: 'Apply for housing (transitional or family)', desc: 'Transitional housing waitlists often take 4–8 weeks. Apply now.', priority: 'high' },
      { id: 't7', title: 'Research second-chance employers', desc: 'Browse Jobs module — identify 3 target employers in your field.', priority: 'high' },
      { id: 't8', title: 'Complete first certification module', desc: 'Finish the first credential-eligible module in your pathway.', priority: 'high' },
      { id: 't9', title: 'Begin SNAP pre-enrollment research', desc: 'Find your local SNAP office and understand eligibility criteria.', priority: 'medium' },
      { id: 't10', title: 'Maintain a 7-day learning streak', desc: 'Daily learning habit will earn a streak bonus and Consistency Badge.', priority: 'medium' },
    ]
  },
  {
    id: '30d',
    label: '30 Days',
    sublabel: 'Preparation',
    color: '#F59E0B',
    tasks: [
      { id: 't11', title: 'Order birth certificate (if needed)', desc: 'Order online or by mail from your state\'s vital records office ($10–$25).', priority: 'high' },
      { id: 't12', title: 'Draft your resume', desc: 'Use Credentials page to export your verified credentials to your resume.', priority: 'high' },
      { id: 't13', title: 'Send 2 job applications', desc: 'Apply via the Jobs module — applications can be submitted before release.', priority: 'high' },
      { id: 't14', title: 'Confirm housing placement', desc: 'Confirm your housing arrangement — do not leave this until release day.', priority: 'high' },
      { id: 't15', title: 'Open second-chance bank account (pre-plan)', desc: 'Identify a "Bank On" certified bank near your release location.', priority: 'medium' },
    ]
  },
  {
    id: '14d',
    label: '14 Days',
    sublabel: 'Final Prep',
    color: '#EF4444',
    tasks: [
      { id: 't16', title: 'Confirm ID appointment or DMV plan', desc: 'Schedule a DMV appointment for your state ID within the first week of release.', priority: 'high' },
      { id: 't17', title: 'Confirm transportation plan', desc: 'Identify who or what will get you from the facility to your first location.', priority: 'high' },
      { id: 't18', title: 'Review parole/probation conditions', desc: 'Know your check-in schedule, travel restrictions, and reporting officer.', priority: 'high' },
      { id: 't19', title: 'Convert credits to Reentry Savings', desc: 'Go to Rewards and convert your remaining credits to cash savings.', priority: 'medium' },
      { id: 't20', title: 'Say goodbye + get peer contact info', desc: 'Exchange contact info with study group members and mentors.', priority: 'low' },
    ]
  },
  {
    id: 'release',
    label: 'Release Day',
    sublabel: 'Day Zero',
    color: '#22C55E',
    tasks: [
      { id: 't21', title: 'Collect all release documents', desc: 'Release certificate, parole conditions, any certificates from programs.', priority: 'high' },
      { id: 't22', title: 'Go directly to confirmed housing', desc: 'Do not deviate from your housing plan on day one.', priority: 'high' },
      { id: 't23', title: 'Report to parole/probation officer (if required)', desc: 'Check your conditions — some require same-day or next-day reporting.', priority: 'high' },
      { id: 't24', title: 'Contact your support network', desc: 'Let your family and supporters know you are safe and have a plan.', priority: 'medium' },
    ]
  },
  {
    id: 'week1',
    label: 'Week 1',
    sublabel: 'Launch',
    color: '#3B82F6',
    tasks: [
      { id: 't25', title: 'Get state ID or driver\'s license', desc: 'Visit DMV with release paperwork. Most states issue same-day temporary ID.', priority: 'high' },
      { id: 't26', title: 'Apply for SNAP', desc: 'Visit your local SNAP office or apply online. Benefits can start within days.', priority: 'high' },
      { id: 't27', title: 'Apply for Medicaid/health insurance', desc: 'Enrollment is available year-round for individuals returning from incarceration.', priority: 'high' },
      { id: 't28', title: 'Open bank account', desc: 'Bring your ID and release documents to a "Bank On" bank.', priority: 'high' },
      { id: 't29', title: 'Follow up on job applications', desc: 'Call or email the employers you applied to from inside. Introduce yourself.', priority: 'high' },
      { id: 't30', title: 'Continue learning on Assistiv OS', desc: 'Log in, maintain your streak, and complete your pathway.', priority: 'medium' },
    ]
  }
];

function PhaseNode({ phase, isActive, isCompleted, isPast, isLocked, completedCount, totalTasks, onClick, isExpanded }) {
  const nodeColor = isCompleted ? '#22C55E' : isActive ? phase.color : isLocked ? '#333333' : '#555555';
  const textColor = isCompleted ? '#22C55E' : isActive ? phase.color : isLocked ? '#444444' : '#666666';
  const bgColor = isCompleted ? 'rgba(34,197,94,0.1)' : isActive ? `rgba(249,115,22,0.1)` : 'transparent';
  const borderColor = isCompleted ? 'rgba(34,197,94,0.3)' : isActive ? 'rgba(249,115,22,0.4)' : '#2A2A2A';

  const pct = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const circumference = 2 * Math.PI * 16;
  const dashOffset = circumference - (pct / 100) * circumference;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', minWidth: 0 }}>
      {/* Node circle with SVG progress ring */}
      <button
        onClick={onClick}
        disabled={isLocked}
        aria-expanded={!isLocked ? isExpanded : undefined}
        aria-label={`${phase.label} — ${phase.sublabel}: ${completedCount}/${totalTasks} tasks ${isLocked ? '(locked)' : isCompleted ? '(completed)' : isActive ? '(active)' : ''}`}
        style={{
          width: '44px', height: '44px', borderRadius: '50%',
          background: bgColor, border: `2px solid ${borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: isLocked ? 'default' : 'pointer', position: 'relative', zIndex: 2,
          transition: 'all 0.25s ease', outline: 'none',
          boxShadow: isActive ? `0 0 0 4px rgba(249,115,22,0.15), 0 0 20px rgba(249,115,22,0.2)` : isCompleted ? '0 0 0 4px rgba(34,197,94,0.12)' : 'none'
        }}
        onMouseEnter={e => { if (!isLocked) e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {/* SVG Ring */}
        {!isLocked && pct > 0 && (
          <svg style={{ position: 'absolute', inset: '-3px', width: '50px', height: '50px' }} viewBox="0 0 44 44" aria-hidden="true">
            <circle cx="22" cy="22" r="16" fill="none" stroke="#222222" strokeWidth="2.5" />
            <circle cx="22" cy="22" r="16" fill="none" stroke={nodeColor} strokeWidth="2.5"
              strokeDasharray={circumference} strokeDashoffset={dashOffset}
              strokeLinecap="round" transform="rotate(-90 22 22)"
              style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
          </svg>
        )}
        {isLocked
          ? <Lock size={14} color="#444444" aria-hidden="true" />
          : isCompleted
            ? <CheckCircle size={16} color="#22C55E" aria-hidden="true" />
            : <span style={{ fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, color: nodeColor }}>{pct}%</span>
        }
      </button>

      {/* Label below node */}
      <div style={{ marginTop: '10px', textAlign: 'center', width: '80px' }}>
        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px', color: textColor, lineHeight: 1.2 }}>{phase.label}</div>
        <div style={{ fontSize: '10px', color: '#444444', lineHeight: 1.3 }}>{phase.sublabel}</div>
      </div>
    </div>
  );
}

function TaskRow({ task, completed, onToggle }) {
  return (
    <li
      style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #1E1E1E', cursor: 'pointer', alignItems: 'flex-start' }}
      onClick={onToggle}
      data-testid={`timeline-task-${task.id}`}
    >
      <div style={{ flexShrink: 0, marginTop: '1px' }}>
        {completed
          ? <CheckCircle size={18} color="#22C55E" aria-label="Completed" />
          : <Circle size={18} color={task.priority === 'high' ? '#F97316' : '#444444'} aria-label="Incomplete" />
        }
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
          <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: completed ? '#555555' : '#F5F5F5', textDecoration: completed ? 'line-through' : 'none' }}>
            {task.title}
          </span>
          {task.priority === 'high' && !completed && (
            <span className="badge-orange" style={{ fontSize: '10px', padding: '1px 6px' }}>
              HIGH
            </span>
          )}
        </div>
        <p style={{ fontSize: '13px', color: '#666666', margin: 0, lineHeight: 1.5 }}>{task.desc}</p>
      </div>
    </li>
  );
}

export default function ReentryTimeline({ releaseDate, daysRemaining }) {
  const [completedTasks, setCompletedTasks] = useState(new Set(['t1', 't2']));
  const [expandedPhase, setExpandedPhase] = useState(null);

  // Determine active phase based on days remaining
  const getActivePhaseIndex = () => {
    if (!daysRemaining && daysRemaining !== 0) return 0;
    if (daysRemaining > 90) return 0;
    if (daysRemaining > 60) return 1;
    if (daysRemaining > 30) return 2;
    if (daysRemaining > 14) return 3;
    if (daysRemaining > 0) return 4;
    return 5; // Post-release
  };

  const activeIndex = getActivePhaseIndex();

  const toggleTask = (taskId) => {
    setCompletedTasks(prev => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  };

  const togglePhase = (phaseId) => {
    setExpandedPhase(prev => prev === phaseId ? null : phaseId);
  };

  const totalCompleted = completedTasks.size;
  const totalTasks = PHASES.reduce((s, p) => s + p.tasks.length, 0);
  const overallPct = Math.round((totalCompleted / totalTasks) * 100);

  const completedPhases = activeIndex;

  return (
    <div data-testid="reentry-timeline">
      {/* Overall Progress Bar */}
      <div style={{ background: '#111111', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Star size={16} color="#F97316" aria-hidden="true" />
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: '#FFFFFF' }}>
              Overall Timeline Progress
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {releaseDate && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#A3A3A3' }}>
                <Calendar size={13} aria-hidden="true" />
                <span>{daysRemaining !== null ? `${daysRemaining} days` : 'No release date set'}</span>
              </div>
            )}
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '22px', color: '#F97316' }}>{overallPct}%</span>
          </div>
        </div>
        <div className="progress-bar" role="progressbar" aria-valuenow={overallPct} aria-valuemin={0} aria-valuemax={100} aria-label={`Overall reentry progress: ${overallPct}%`}>
          <div className="progress-fill" style={{ width: `${overallPct}%` }} />
        </div>
        <p style={{ fontSize: '12px', color: '#555555', margin: '8px 0 0', fontFamily: 'Inter, sans-serif' }}>
          {totalCompleted} of {totalTasks} timeline tasks complete
        </p>
      </div>

      {/* Phase Track — Desktop: horizontal, Mobile: vertical stack */}
      <div style={{ background: '#111111', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '24px', marginBottom: '16px', overflowX: 'auto' }}>
        <div
          className="timeline-track"
          style={{ '--progress': `${(activeIndex / (PHASES.length - 1)) * 100}%` }}
          role="list"
          aria-label="Reentry phases"
        >
          {PHASES.map((phase, idx) => {
            const phaseTasks = phase.tasks;
            const completedCount = phaseTasks.filter(t => completedTasks.has(t.id)).length;
            const isCompleted = idx < activeIndex || completedCount === phaseTasks.length;
            const isActive = idx === activeIndex;
            const isLocked = idx > activeIndex + 1;
            return (
              <PhaseNode
                key={phase.id}
                phase={phase}
                isActive={isActive}
                isCompleted={isCompleted}
                isPast={idx < activeIndex}
                isLocked={isLocked}
                completedCount={completedCount}
                totalTasks={phaseTasks.length}
                isExpanded={expandedPhase === phase.id}
                onClick={() => !isLocked && togglePhase(phase.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Expanded Phase Card */}
      {expandedPhase && (() => {
        const phase = PHASES.find(p => p.id === expandedPhase);
        if (!phase) return null;
        return (
          <div
            style={{ background: '#111111', border: `1px solid ${phase.color}44`, borderRadius: '14px', padding: '20px', marginBottom: '16px', animation: 'fadeIn 0.2s ease-out' }}
            role="region"
            aria-label={`${phase.label} tasks`}
            data-testid={`phase-tasks-${phase.id}`}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: '#FFFFFF', margin: 0 }}>
                {phase.label} — {phase.sublabel}
              </h3>
              <button
                onClick={() => setExpandedPhase(null)}
                aria-label="Close phase details"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#555555', display: 'flex', alignItems: 'center' }}
              >
                <ChevronUp size={18} aria-hidden="true" />
              </button>
            </div>
            <p style={{ fontSize: '13px', color: '#666666', margin: '0 0 16px' }}>
              {phase.tasks.filter(t => completedTasks.has(t.id)).length}/{phase.tasks.length} tasks complete
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {phase.tasks.map(task => (
                <TaskRow
                  key={task.id}
                  task={task}
                  completed={completedTasks.has(task.id)}
                  onToggle={() => toggleTask(task.id)}
                />
              ))}
            </ul>
          </div>
        );
      })()}

      {/* Active Phase Quick View */}
      {!expandedPhase && (
        <div style={{ background: '#111111', border: '1px solid #1E1E1E', borderRadius: '14px', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: '#FFFFFF' }}>
              Now — {PHASES[activeIndex].label}: {PHASES[activeIndex].sublabel}
            </span>
            <button
              onClick={() => togglePhase(PHASES[activeIndex].id)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', color: '#F97316', fontSize: '13px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}
              data-testid="view-phase-tasks-btn"
            >
              View Tasks <ArrowRight size={13} aria-hidden="true" />
            </button>
          </div>

          {PHASES[activeIndex].tasks.filter(t => !completedTasks.has(t.id)).slice(0, 3).map(task => (
            <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #1A1A1A' }}>
              <Circle size={16} color={task.priority === 'high' ? '#F97316' : '#444444'} aria-hidden="true" />
              <span style={{ fontSize: '14px', color: '#CCCCCC', fontFamily: 'Inter, sans-serif', flex: 1 }}>{task.title}</span>
              {task.priority === 'high' && <span className="badge-orange" style={{ fontSize: '10px' }}>PRIORITY</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
